<template>
  <div
    class="exchange-rates-currencies"
    :class="{ 'is-horizontal': isHorizontal, 'to-left': toLeft }"
  >
    <button
      v-for="item in favItems"
      :key="item.code"
      class="exchange-rates-currencies__item exchange-rates-currencies__item-btn"
      :class="{ 'is-selected': selectedValue === item.code }"
      @click="handleSelect(item)"
      data-testid="currencybutton"
    >
      {{ item.code }}
    </button>

    <button
      v-if="secondaryItem"
      class="exchange-rates-currencies__item exchange-rates-currencies__item-btn"
      :class="{ 'is-selected': selectedValue === secondaryItem.code }"
      @click="handleSelect(secondaryItem)"
      data-testid="currencybutton"
    >
      {{ secondaryItem.code }}
    </button>

    <div
      ref="dropdown"
      class="exchange-rates-currencies__item"
      data-testid="searchmore"
    >
      <button
        class="exchange-rates-dropdown__toggle exchange-rates-currencies__item-btn"
        :class="{ 'is-open': isOpen }"
        @click="handleOpen"
        aria-label="Show currencies"
      >
        <Icon icon="dots-h" />
      </button>
      <div
          class="exchange-rates-dropdown"
          ref="dropdownList"
          :class="{
            'to-top': directionTop,
            'is-empty': isEmptySearch
          }"
      >
        <div class="exchange-rates-dropdown__inner">
          <div class="exchange-rates-dropdown__mobile-head">
            <div class="exchange-rates-dropdown__mobile-title"
            >{{ $t('shared.currencyPicker.title') }}</div>
            <button
                class="exchange-rates-dropdown__close-btn"
                @click="handleOpen"
            >
              <i class="icon icon-close-lg"></i>
            </button>
          </div>

          <div class="exchange-rates-dropdown-search">
            <input
                ref="search"
                type="text"
                :placeholder="$t('shared.currencyPicker.searchPlaceholder')"
                class="exchange-rates-dropdown-search__input"
                :value="searchString"
                @input="searchString = $event.target.value"
                @keyup.enter="selectFirstOption"
            />
          </div>
          <div class="exchange-rates-dropdown-lists" :class="{ 'loading-data': loading }">
            <div class="exchange-rates-dropdown-lists__empty-message"
            >{{ $t('shared.currencyPicker.emptySearchMessage') }}</div>
            <div class="exchange-rates-dropdown-primary">
              <div
                  ref="primaryList"
                  class="exchange-rates-dropdown-primary__list-wrapper after-fade">
                <ul
                    ref="primaryListScroll"
                    class="exchange-rates-dropdown-primary__list"
                >
                  <template v-if="isOpen">
                  <li v-for="(item, i) in filteredItems" :key="i">
                    <button
                        class="exchange-rates-dropdown-secondary__item"
                        :class="{
                          'is-selected': selectedValue === item.code,
                          'is-hover': i === 0
                        }"
                        @click="handleSelect(item)"
                    >
                      <span class="exchange-rates-dropdown-secondary__item-label">
                          {{ item.code }}
                      </span>
                      <span class="exchange-rates-dropdown-secondary__item-details">
                        {{ item.name }}
                        </span>
                    </button>
                  </li>
                  </template>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Shared/Icon';

export default {
  name: 'ExchangeRatesCurrencySelect',

  components: {
    Icon,
  },

  props: {
    items: { type: Array, default: () => [] },
    selectedItem: { type: Object, default: () => {} },
    isHorizontal: { type: Boolean, required: false, default: false },
    toLeft: { type: Boolean, required: false, default: false },
    loading: { type: Boolean, default: false },
  },

  data () {
    return {
      isOpen: false,
      isFavCount: 2,
      searchString: '',
    };
  },

  watch: {
    filteredItems (newFilteredItems) {
      this.$nextTick(() => this.handlePrimaryListFade());
    },

    isOpen () {
      if (!this.$isServer && window.innerWidth < 1000) {
        this.$bus.$emit('FREEZE_BODY', this.isOpen);
      }
    },
  },

  computed: {
    directionTop () {
      if (!this.isOpen) {
        return false;
      }

      return this.dropdownVerticalPosition();
    },

    isEmptySearch () {
      return !this.loading && this.filteredItems.length === 0;
    },

    filteredItems () {
      return this.listItems.filter((item) => {
        return item.name.toLowerCase().includes(this.searchString.toLowerCase()) ||
            item.code.toLowerCase().includes(this.searchString.toLowerCase());
      });
    },

    favItems () {
      return this.items.filter((item, index) => index < this.isFavCount);
    },

    secondaryItem() {
      if (this.favItems.some(item => item.code === this.selectedItem.code)) {
        return;
      }

      return this.selectedItem;
    },

    listItems () {
      return this.items.filter((item, index) => index > this.isFavCount - 1);
    },

    selectedValue () {
      return this.selectedItem ? this.selectedItem.code : null;
    },
  },

  mounted () {
    this.$refs.primaryListScroll.addEventListener(
      'scroll',
      this.handlePrimaryListFade,
      true,
    );
  },

  destroyed () {
    document.removeEventListener('click', this.handleCloseAll);
  },

  methods: {
    selectFirstOption () {
      if (this.filteredItems.length === 0) {
        return;
      }
      const first = this.filteredItems[0];
      this.handleSelect(first);
      this.handleClose();
    },

    handleOpen (event) {
      event.preventDefault();
      this.isOpen = !this.isOpen;
      this.handlePrimaryListFade();

      if (this.isOpen) {
        this.$emit('dropDownOpen');
        this.$nextTick(() => this.$refs.search.focus());

        document.addEventListener('click', this.handleCloseAll);
      } else {
        document.removeEventListener('click', this.handleCloseAll);
      }
    },

    dropdownVerticalPosition () {
      const bottomSpace =
        window.innerHeight - this.$refs.dropdown.getBoundingClientRect().top;

      const toTop =
        bottomSpace < this.$refs.dropdownList.getBoundingClientRect().height;

      return toTop;
    },

    handleClose () {
      this.isOpen = false;
      this.searchString = '';
      document.removeEventListener('click', this.handleCloseAll);
    },

    handleCloseAll (e) {
      const el = this.$refs.dropdown;
      const target = e.target;

      if (el !== target && !el.contains(target)) {
        this.handleClose();
      }
    },

    handleSelect (item) {
      this.$emit('updated', item);

      if (this.items.findIndex((el, index) => item.code === el.code && index < this.isFavCount) + 1) return;

      this.handleClose();
    },

    handlePrimaryListFade () {
      const itemsCount = this.filteredItems.length;
      const list = this.$refs.primaryListScroll;
      const listWrapper = this.$refs.primaryList;
      const visibleItemsCount = 12;

      if (list.scrollTop <= 10 && listWrapper.classList.contains('pre-fade')) {
        listWrapper.classList.remove('pre-fade');
      }

      if (list.scrollTop > 10 && !listWrapper.classList.contains('pre-fade')) {
        listWrapper.classList.add('pre-fade');
      }

      const totalScrollHeight = list.scrollTop + list.clientHeight;
      const minScrollHeight = list.scrollHeight - 10;
      const hasFadeClass = listWrapper.classList.contains('after-fade');
      if (
        (itemsCount <= visibleItemsCount || totalScrollHeight >= minScrollHeight) && hasFadeClass
      ) {
        listWrapper.classList.remove('after-fade');
      }

      if (
        (itemsCount > visibleItemsCount && totalScrollHeight < minScrollHeight) && hasFadeClass
      ) {
        listWrapper.classList.add('after-fade');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.exchange-rates-currencies {
  width: 100%;

  &.is-horizontal {
    display: flex;
    width: auto;
  }

  &__item-btn {
    display: block;
    line-height: rem(24);
    border-radius: rem(4);
    font-family: $font-bold;
    font-size: rem(14);
    padding: 0 rem(4);
    color: $grey;
    min-width: rem(40);
    text-align: center;
    transition: background-color 0.2s linear;
  }

  &__item-btn:hover {
    background-color: $grey-04;
  }

  &__item-btn.is-selected {
    background-color: $beige;
    color: $black;
    pointer-events: none;
  }

  &.is-horizontal &__item-btn {
    line-height: rem(28);
  }

  &.is-horizontal &__item-btn + &__item-btn {
    margin-left: rem(8);
  }

  [dir='rtl'] &.is-horizontal &__item-btn + &__item-btn {
    margin-right: rem(8);
    margin-left: unset;
  }

  @media screen and (max-width: $screen-md-max) {
    display: flex;
    justify-content: center;
    align-items: center;

    &__item + &__item {
      margin-left: rem(8);
    }

    [dir='rtl'] &__item + &__item {
      margin-right: rem(8);
      margin-left: unset;
    }
  }

  @media screen and (min-width: $screen-lg-min) {
    width: cols('lg', 1, true);
    min-width: cols('lg', 1, true);

    &.is-horizontal {
      width: auto;
      min-width: 0;
    }

    &__item + &__item {
      margin-top: rem(2);
    }

    &.is-horizontal &__item-btn + &__item-btn {
      margin-top: 0;
      margin-left: rem(8);
    }

    [dir='rtl'] &.is-horizontal &__item-btn + &__item-btn {
      margin-right: rem(8);
      margin-left: unset;
    }
  }

  @media screen and (min-width: $screen-xlg-min) {
    width: cols('xlg', 1, true);
    min-width: cols('xlg', 1, true);

    &.is-horizontal {
      width: auto;
      min-width: 0;
    }
  }
}
.exchange-rates-dropdown {
  @media screen and (min-width: $screen-lg-min) {
    .to-left &,
    [dir='rtl'] & {
      left: auto;
      right: rem(-14);
    }

    [dir='rtl'] .to-left & {
      right: auto;
      left: rem(-14);
    }
  }
}

.exchange-rates-dropdown-lists {
  display: flex;
  width: 100%;

  &__empty-message {
    display: none;
    text-align: center;
    padding: rem(8);
  }

  .is-empty &__empty-message {
    display: block;
  }

  &.loading-data {
    position: relative;

    &:before {
      content: '';
      display: block;
      height: rem(80);
    }
    &:after {
      content: '';
      display: block;
      position: absolute;
      left: 50%;
      margin-left: rem(-16);
      margin-top: rem(16);
      margin-bottom: rem(32);
      width: rem(32);
      height: rem(32);
      background-image: url('data:image/png;base64,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');
      background-size: contain;
      background-position: center center;
      background-repeat: no-repeat;
      animation: spin 2s linear infinite;
    }

    li {
      display: none;
    }
  }

  @media screen and (max-width: $screen-md-max) {
    flex: 1;
    overflow: hidden;

    &__empty-message {
      min-width: rem(210);
      width: 100%;
      padding: 0 0 0 rem(16);
      max-height: 100%;
    }
  }

  @media screen and (min-width: $screen-lg-min) {
    padding-top: rem(40);

    &__empty-message {
      min-width: rem(350);
      max-height: rem(286);
    }
  }
}
</style>
